import type { ReportUsage } from '@ms/yammer-telemetry';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import React, { useMemo } from 'react';

import { EntityWrapperContext } from './EntityWrapperContext';
import { SessionContext } from './SessionContext';
import { TelemetryContext, emptyReportUsage } from './TelemetryContext';
import { passthroughRenderer } from './renderer';
import type { RenderGroupReferenceType, RenderUserReferenceType } from './renderer/types';

interface SessionContextProviderProps {
  readonly baseHost?: string;
  readonly renderGroupReference?: RenderGroupReferenceType;
  readonly renderUserReference?: RenderUserReferenceType;
  readonly reportUsage?: ReportUsage;
  readonly children: ReactNode;
}

const SessionContextProvider: FC<PropsWithChildren<SessionContextProviderProps>> = ({
  baseHost = '',
  renderGroupReference = passthroughRenderer,
  renderUserReference = passthroughRenderer,
  reportUsage = emptyReportUsage,
  children,
}) => {
  const sessionContextValue = useMemo(() => ({ baseHost }), [baseHost]);
  const telemetryContextValue = useMemo(() => ({ reportUsage }), [reportUsage]);
  const entityWrapperContextValue = useMemo(
    () => ({ renderGroupReference, renderUserReference }),
    [renderGroupReference, renderUserReference]
  );

  return (
    <SessionContext.Provider value={sessionContextValue}>
      <EntityWrapperContext.Provider value={entityWrapperContextValue}>
        <TelemetryContext.Provider value={telemetryContextValue}>{children}</TelemetryContext.Provider>
      </EntityWrapperContext.Provider>
    </SessionContext.Provider>
  );
};

export default SessionContextProvider;
